import React from "react";
import { View, Text, Modal, TouchableOpacity, Image } from "react-native";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import { StackActions, useNavigation } from "@react-navigation/native";
import { navigateToScreen } from "../../../../utility/NavigationHelper";
import { CommonButton } from "../../../../common/CommonButton";
import { AppColors } from "../../../../utility/AppColors";
import { AppFontStyle } from "../../../../styles/AppFontStyle";
import { AppImages } from "../../../../utility/AppImages";
import { AppScreens } from "../../../Utility/AppScreens";

interface EventsProps {
  showModal: boolean;
  setShowModal: (value: boolean) => void;
  eventID: string;
  onClose: () => void;
}

const ChangesRejectedModal: React.FC<EventsProps> = ({
  showModal = false,
  setShowModal,
  eventID,
  onClose,
}) => {
  const navigation = useNavigation();

  const onCloseClick = () => {
    setShowModal(false);
    navigateToScreen(navigation, AppScreens.UpdatePublicEventScreen, { eventID });
    onClose();
  };

  const styles = useStyles();
  const deviceSize = useDeviceSize();

  return (
    <Modal
      animationType="fade"
      transparent={true}
      visible={showModal}
      onRequestClose={onCloseClick}
    >
      <View style={styles.container}>
        <View style={styles.modalView}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.headerText}>Changes Rejected</Text>
            <TouchableOpacity onPress={onCloseClick}>
              <Image
                source={AppImages.icCloseWhite}
                style={{
                  height:
                    deviceSize === DEVICE_SIZES.SM ||
                    deviceSize === DEVICE_SIZES.XS
                      ? 20
                      : 25,
                  width:
                    deviceSize === DEVICE_SIZES.SM ||
                    deviceSize === DEVICE_SIZES.XS
                      ? 20
                      : 25,
                }}
              />
            </TouchableOpacity>
          </View>

          {/* Divider */}
          <View style={styles.separator} />

          {/* Description */}
          <View style={styles.small}>
            <View style={styles.content}>
              <Image source={AppImages.icReject} style={styles.logo} />
              <Text style={styles.description}>
                The Challenger has rejected your event updates.
              </Text>
              <Text style={styles.description}>
                Please review and make the necessary adjustments.
              </Text>
            </View>
          </View>

          {/* Buttons */}
          <View style={styles.separator} />
          <View style={styles.buttonContainer}>
            <CommonButton
              title="Edit Details"
              onTap={onCloseClick}
              buttonStyle={styles.cancelButton}
              textStyle={styles.cancelText}
            />
            <CommonButton
              title="Visit Event"
              onTap={() => {
                navigateToScreen(navigation, AppScreens.OnGoingEvents, { eventID });
                setShowModal(false);
              }}
              buttonStyle={styles.submitButton}
              textStyle={styles.submitText}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
};

const useStyles = CreateResponsiveStyle(
  {
    container: {
      flex: 1,
      backgroundColor: "rgba(0, 0, 0, 0.7)",
      justifyContent: "center",
      alignItems: "center",
    },

    modalView: {
      width: "90%",
      maxWidth: 800,
      backgroundColor: "#18151B",
      borderRadius: 12,
      padding: 20,
      alignItems: "center",
    },

    small: {
      backgroundColor: AppColors.purple_453B50,
      padding: 16,
      borderRadius: 24,
      marginBottom: 20,
      marginTop: 20,
      width: "100%",
    },
    header: {
      flexDirection: "row",
      width: "100%",
      justifyContent: "space-between",
      alignItems: "center",
    },
    headerText: {
      ...AppFontStyle.BOLD_ANYBODY_24,
      color: AppColors.white,
    },
    closeIcon: {
      height: 28,
      width: 28,
    },
    separator: {
      width: "100%",
      height: 2,
      backgroundColor: "#333",
      marginTop: 5,
    },
    content: {
      alignItems: "center",
      marginVertical: 15,
    },
    logo: {
      height: 145,
      width: 145,
      marginBottom: 10,
    },
    description: {
      ...AppFontStyle.REGULAR_ANYBODY_24,
      color: "#FFFFFF",
      textAlign: "center",
      marginBottom: 5,
    },

    buttonContainer: {
      flexDirection: "row",
      justifyContent: "space-between",
      width: "100%",
      marginTop: 10,
    },
    cancelButton: {
      backgroundColor: AppColors.white,
      borderRadius: 10,
      height: 38,
      paddingHorizontal: 12,
      shadowOpacity: 0,
      paddingVertical: 5,
    },
    cancelText: {
      color: AppColors.black,
      fontSize: 14,
    },
    submitButton: {
      backgroundColor: AppColors.buttonRed_DC2626,
      borderRadius: 10,
      height: 40,
      shadowOpacity: 0,
      paddingVertical: 5,
      paddingHorizontal: 15,
    },
    submitText: {
      color: AppColors.white,
      fontSize: 14,
    },
  },
  {
    [DEVICE_SIZES.SM]: {
      container: {
        flex: 1,
        backgroundColor: AppColors.blackTrans_000000a8,
        justifyContent: "center",
        alignItems: "center",
      },
      modalView: {
        width: "70%",
        backgroundColor: AppColors.black_121212,
        marginHorizontal: 30,
        alignSelf: "center",
        borderRadius: 12,
      },
      small: {
        backgroundColor: AppColors.purple_453B50,
        padding: 8,
        borderRadius: 24,
        marginBottom: 7,
        marginTop: 9,
        width: "100%",
      },

      content: {
        alignItems: "center",
        marginVertical: 0,
      },
      header: {
        flexDirection: "row",
        width: "100%",
        justifyContent: "space-between",
        alignItems: "center",
      },
      headerText: {
        ...AppFontStyle.BOLD_ANYBODY_16,
        color: AppColors.white,
      },
      logo: {
        height: 60,
        width: 60,
        marginBottom: 10,
      },
      description: {
        ...AppFontStyle.REGULAR_ANYBODY_14,
        color: "#FFFFFF",
        textAlign: "center",
        marginBottom: 0,
      },

      buttonContainer: {
        flexDirection: "row",
        justifyContent: "space-between",
        width: "100%",
      },
      cancelButton: {
        backgroundColor: AppColors.white,
        borderRadius: 10,
        height: 30,
        paddingHorizontal: 10,
        shadowOpacity: 0,
        paddingVertical: 5,
      },
      cancelText: {
        color: AppColors.black,
        fontSize: 12,
      },
      submitButton: {
        backgroundColor: AppColors.buttonRed_DC2626,
        borderRadius: 10,
        height: 30,
        shadowOpacity: 0,
        paddingVertical: 5,
        paddingHorizontal: 10,
      },
      submitText: {
        color: AppColors.white,
        fontSize: 12,
      },
    },

    [DEVICE_SIZES.XS]: {
      container: {
        flex: 1,
        backgroundColor: AppColors.blackTrans_000000a8,
        justifyContent: "center",
        alignItems: "center",
      },
      modalView: {
        width: "95%",
        backgroundColor: AppColors.black_121212,
        marginHorizontal: 30,
        alignSelf: "center",
        borderRadius: 12,
      },
      small: {
        backgroundColor: AppColors.purple_453B50,
        padding: 8,
        borderRadius: 24,
        marginBottom: 7,
        marginTop: 9,
        width: "100%",
      },

      content: {
        alignItems: "center",
        marginVertical: 0,
      },
      header: {
        flexDirection: "row",
        width: "100%",
        justifyContent: "space-between",
        alignItems: "center",
      },
      headerText: {
        ...AppFontStyle.BOLD_ANYBODY_16,
        color: AppColors.white,
      },
      logo: {
        height: 60,
        width: 60,
        marginBottom: 10,
      },
      description: {
        ...AppFontStyle.REGULAR_ANYBODY_14,
        color: "#FFFFFF",
        textAlign: "center",
        marginBottom: 0,
      },

      buttonContainer: {
        flexDirection: "row",
        justifyContent: "space-between",
        width: "100%",
      },
      cancelButton: {
        backgroundColor: AppColors.white,
        borderRadius: 10,
        height: 30,
        paddingHorizontal: 10,
        shadowOpacity: 0,
        paddingVertical: 5,
      },
      cancelText: {
        color: AppColors.black,
        fontSize: 12,
      },
      submitButton: {
        backgroundColor: AppColors.buttonRed_DC2626,
        borderRadius: 10,
        height: 30,
        shadowOpacity: 0,
        paddingVertical: 5,
        paddingHorizontal: 10,
      },
      submitText: {
        color: AppColors.white,
        fontSize: 12,
      },
    },
  }
);

export default ChangesRejectedModal;
